import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { motion } from "framer-motion";
import { 
  ArrowRight,
  FileText
} from "lucide-react";
import { MarketingNav, MarketingFooter } from "./LandingPage";

const fadeIn = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5 }
};

export default function TermsOfServicePage() {
  return (
    <div className="min-h-screen bg-[#f5f5f0]">
      <MarketingNav />

      {/* Hero Section */}
      <section className="pt-32 pb-12 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-6">
              <FileText className="w-8 h-8 text-primary" />
            </div>
            <h1 className="text-4xl lg:text-5xl font-bold text-foreground leading-tight mb-4">
              Terms of Service
            </h1>
            <p className="text-muted-foreground">
              Last Updated: 2/20/2026
            </p>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-12 px-6">
        <div className="max-w-3xl mx-auto">
          <Card className="rounded-2xl border-none shadow-[0_4px_20px_rgb(0,0,0,0.04)] bg-white p-8 lg:p-12">
            <div className="prose prose-gray max-w-none">
              
              <h2 className="text-xl font-bold text-foreground mt-0 mb-4">
                1. Agreement
              </h2>
              <p className="text-muted-foreground mb-4">
                These Terms of Service ("Terms") govern your use of HeyIntake (the "Service"), operated by Okliko Inc., a Delaware-incorporated company.
              </p>
              <p className="text-muted-foreground mb-8">
                By creating an account or using the Service, you agree to these Terms.
              </p>

              <h2 className="text-xl font-bold text-foreground mt-8 mb-4">
                2. Eligibility
              </h2>
              <p className="text-muted-foreground mb-8">
                You must be at least 18 years old and legally capable of entering into binding contracts.
              </p>

              <h2 className="text-xl font-bold text-foreground mt-8 mb-4">
                3. Account Registration
              </h2>
              <p className="text-muted-foreground mb-2">You agree to:</p>
              <ul className="text-muted-foreground mb-4 list-disc pl-6 space-y-1">
                <li>Provide accurate information</li>
                <li>Maintain account security</li>
                <li>Notify us of unauthorized access</li>
              </ul>
              <p className="text-muted-foreground mb-8">
                You are responsible for activity under your account.
              </p>

              <h2 className="text-xl font-bold text-foreground mt-8 mb-4">
                4. Subscription and Billing
              </h2>
              <p className="text-muted-foreground mb-4">
                HeyIntake offers subscription-based plans with a paid trial period.
              </p>
              <p className="text-muted-foreground mb-2">By subscribing, you agree to:</p>
              <ul className="text-muted-foreground mb-4 list-disc pl-6 space-y-1">
                <li>Start a 7-day free trial</li>
                <li>Receive 7 days of full access during the trial period</li>
                <li>Automatic renewal at the full plan price on day 8 unless canceled</li>
                <li>Recurring monthly billing thereafter until cancellation</li>
                <li>Pricing subject to change with notice</li>
              </ul>
              <p className="text-muted-foreground mb-4">
                Lead limits apply according to your selected plan.
              </p>
              <p className="text-muted-foreground mb-4">
                <strong>Cancellation:</strong> You may cancel your subscription at any time. If you cancel during the trial period, you will retain access until the end of the 7-day trial. If you cancel after the trial converts to a paid subscription, you will retain access until the end of the current billing period. No prorated refunds are provided.
              </p>
              <p className="text-muted-foreground mb-8">
                Payments are processed through third-party payment providers, including Stripe, Inc. By subscribing, you agree to Stripe's terms and policies where applicable.
              </p>

              <h2 className="text-xl font-bold text-foreground mt-8 mb-4">
                5. Plan Limits
              </h2>
              <p className="text-muted-foreground mb-4">
                Starter and Professional plans include defined monthly submission limits.
              </p>
              <p className="text-muted-foreground mb-2">If you exceed your limit:</p>
              <ul className="text-muted-foreground mb-4 list-disc pl-6 space-y-1">
                <li>Submissions may be restricted</li>
                <li>OR you may upgrade your plan</li>
              </ul>
              <p className="text-muted-foreground mb-8">
                Limits reset each billing cycle.
              </p>

              <h2 className="text-xl font-bold text-foreground mt-8 mb-4">
                6. Acceptable Use
              </h2>
              <p className="text-muted-foreground mb-2">You agree not to:</p>
              <ul className="text-muted-foreground mb-4 list-disc pl-6 space-y-1">
                <li>Use the Service for unlawful purposes</li>
                <li>Transmit malicious code</li>
                <li>Attempt to disrupt platform functionality</li>
                <li>Use the Service to store illegal content</li>
              </ul>
              <p className="text-muted-foreground mb-8">
                We reserve the right to suspend accounts violating these Terms.
              </p>

              <h2 className="text-xl font-bold text-foreground mt-8 mb-4">
                7. AI Functionality Disclaimer
              </h2>
              <p className="text-muted-foreground mb-4">
                Lead scoring and summaries are generated automatically.
              </p>
              <p className="text-muted-foreground mb-4">
                These outputs are informational and should not be relied upon as sole decision-making criteria.
              </p>
              <p className="text-muted-foreground mb-8">
                You remain responsible for business decisions made using the Service.
              </p>

              <h2 className="text-xl font-bold text-foreground mt-8 mb-4">
                8. Data Ownership
              </h2>
              <p className="text-muted-foreground mb-4">
                You retain ownership of your submission data.
              </p>
              <p className="text-muted-foreground mb-8">
                You grant HeyIntake a limited license to process data solely to provide the Service.
              </p>

              <h2 className="text-xl font-bold text-foreground mt-8 mb-4">
                9. Termination
              </h2>
              <p className="text-muted-foreground mb-4">
                You may cancel your subscription at any time.
              </p>
              <p className="text-muted-foreground mb-2">We may suspend or terminate accounts that:</p>
              <ul className="text-muted-foreground mb-4 list-disc pl-6 space-y-1">
                <li>Violate these Terms</li>
                <li>Present security risks</li>
                <li>Engage in abuse of the platform</li>
              </ul>
              <p className="text-muted-foreground mb-8">
                Upon termination, access to data may be revoked.
              </p>

              <h2 className="text-xl font-bold text-foreground mt-8 mb-4">
                10. Disclaimer of Warranties
              </h2>
              <p className="text-muted-foreground mb-4">
                The Service is provided "as is" and "as available."
              </p>
              <p className="text-muted-foreground mb-2">We do not guarantee:</p>
              <ul className="text-muted-foreground mb-8 list-disc pl-6 space-y-1">
                <li>Continuous availability</li>
                <li>Error-free operation</li>
                <li>Specific business outcomes</li>
              </ul>

              <h2 className="text-xl font-bold text-foreground mt-8 mb-4">
                11. Limitation of Liability
              </h2>
              <p className="text-muted-foreground mb-4">
                To the maximum extent permitted by law:
              </p>
              <p className="text-muted-foreground mb-2">Okliko Inc. shall not be liable for:</p>
              <ul className="text-muted-foreground mb-4 list-disc pl-6 space-y-1">
                <li>Indirect damages</li>
                <li>Loss of profits</li>
                <li>Loss of data</li>
                <li>Business interruption</li>
              </ul>
              <p className="text-muted-foreground mb-8">
                Our total liability shall not exceed the amount paid by you in the previous 12 months.
              </p>

              <h2 className="text-xl font-bold text-foreground mt-8 mb-4">
                12. Governing Law
              </h2>
              <p className="text-muted-foreground mb-8">
                These Terms are governed by the laws of the State of Delaware, without regard to conflict of law principles.
              </p>

              <h2 className="text-xl font-bold text-foreground mt-8 mb-4">
                13. Changes to Terms
              </h2>
              <p className="text-muted-foreground mb-4">
                We may modify these Terms at any time.
              </p>
              <p className="text-muted-foreground mb-8">
                Continued use of the Service constitutes acceptance of the revised Terms.
              </p>

              <h2 className="text-xl font-bold text-foreground mt-8 mb-4">
                14. Contact
              </h2>
              <div className="text-muted-foreground mt-6 p-4 bg-muted/30 rounded-lg">
                <p className="font-semibold text-foreground">Okliko Inc.</p>
                <p>1000 Brickell Ave, STE 715</p>
                <p>Miami, FL 33131</p>
                <p><a href="mailto:hello@heyintake.com" className="text-primary hover:underline">hello@heyintake.com</a></p>
              </div>

            </div>
          </Card>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-6 bg-primary">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div {...fadeIn}>
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
              Ready to get started?
            </h2>
            <p className="text-lg text-white/80 mb-8">
              Start your free trial today. No credit card required.
            </p>
            <Link to="/register">
              <Button size="lg" variant="secondary" className="rounded-full px-8 font-semibold">
                Start Free Trial
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
